import { AppDispatch } from '../app/store';
import { UPSELL_METRIC_VERSION } from '../constants';
import { UpsellTriggers } from '../components/OmniBar/Omnibar.constants';
import { InfoTransmissionMessage, messagingActions } from './messagingSlice';
import { ParsedModifierGroup } from '../types/menu';
import { IConstraint } from '../types/brandMenu';
import { Upsell } from '../utils/upsell';
import { TopLevelMenuItem } from '../utils/menu';

export interface GreetingDetails {
  item?: any;
  constraint: IConstraint & { upsell_declined_item?: string };
}

/**
 * Handles greeting upsell logic when the first item is added to cart
 * and the greeting has an upsell prompt.
 *
 * @param greetingDetails - The greeting details containing the constraint
 * @param menuItem - The menu item being added to cart
 * @param isAIEnabled - Whether AI is enabled
 * @param dispatch - Redux dispatch function
 * @param upsellInstance - The upsell instance to use
 */
export function handleGreetingUpsell(
  greetingDetails: GreetingDetails,
  menuItem: TopLevelMenuItem,
  isAIEnabled: boolean,
  dispatch: AppDispatch,
  upsellInstance: Upsell
): void {
  const constraint = greetingDetails?.constraint;
  const { prompt, name } = constraint || {};

  const upsell_attempted = upsellInstance.attempt(
    {
      ...constraint,
      upsell_decline_items: constraint?.upsell_declined_item
        ? [constraint.upsell_declined_item]
        : [],
    } as unknown as ParsedModifierGroup,
    '',
    UpsellTriggers.hitlGreeting,
    prompt ?? '',
    name ?? ''
  );

  if (!isAIEnabled) {
    const payload: Partial<InfoTransmissionMessage> = {
      data: {
        type: 'METRIC',
        message: prompt,
        metadata: {
          intent: name,
          upsell_attempted,
          upsell_metric_version: UPSELL_METRIC_VERSION,
        },
      },
    };
    dispatch(messagingActions.sendInfo(payload as any));
  }

  const upsellAttempted = upsellInstance.getUpsellAttempted() || {};

  let upsell_declined;
  let upsell_accepted;
  let upsell_metric_version;
  let message = '';
  if (upsellAttempted) {
    const {
      upsell_decline_items = [],
      upsell_items = [],
      upsell_phrase = '',
    } = upsellAttempted as any;
    if (upsell_items.includes(menuItem.name)) {
      upsell_accepted = upsellInstance.accept([menuItem.name]);
      message = `Accept greeting upsell - (${upsell_phrase}) with ${menuItem.name}`;
    } else {
      upsell_declined = upsellInstance.reject([menuItem.name]);
      message = `Reject greeting upsell - (${upsell_phrase}) with ${
        upsell_decline_items.join(', ') || 'DECLINED'
      } as ${menuItem.name} is selected`;
    }
    upsell_metric_version = UPSELL_METRIC_VERSION;
  }

  const infoPayload: Partial<InfoTransmissionMessage> = {
    data: {
      type: 'METRIC',
      message,
      metadata: {
        menuItem: {
          id: menuItem.id,
          name: menuItem.name,
          is_upsell: true,
          is_upsell_prompt: true,
        },
        upsell_metric_version,
        upsell_declined,
        upsell_accepted,
      },
    },
  };
  dispatch(messagingActions.sendInfo(infoPayload as any));
}
